// Synthesized retro sound effects via Web Audio (no asset files needed).
import ambient from "./ambientAudio";

let _ctx = null;
const ctx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) _ctx = new AC();
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
};
const allowed = () => { try { return !ambient.muted; } catch { return true; } };

function tone({ freq = 440, dur = 0.15, type = "sine", gain = 0.2, slideTo, delay = 0 }) {
  const c = ctx();
  if (!c || !allowed()) return;
  const t0 = c.currentTime + delay;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t0);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g);
  g.connect(c.destination);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

export const sfx = {
  click() { tone({ freq: 900, dur: 0.06, type: "square", gain: 0.14 }); },          // marble click
  hit() { tone({ freq: 220, dur: 0.13, type: "triangle", gain: 0.26, slideTo: 90 }); }, // gilli thock
  crash() { tone({ freq: 180, dur: 0.18, type: "sawtooth", gain: 0.2, slideTo: 70 }); tone({ freq: 300, dur: 0.12, type: "square", gain: 0.12, delay: 0.03 }); }, // lagori stones
  flip() { tone({ freq: 520, dur: 0.09, type: "sine", gain: 0.14, slideTo: 840 }); }, // book page flip
  bell() { tone({ freq: 1046, dur: 0.45, type: "sine", gain: 0.22 }); tone({ freq: 1568, dur: 0.45, type: "sine", gain: 0.1, delay: 0.02 }); },
  spin() { tone({ freq: 200, dur: 0.5, type: "sawtooth", gain: 0.12, slideTo: 620 }); },
  beep() { tone({ freq: 660, dur: 0.05, type: "square", gain: 0.12 }); },
  eat() { tone({ freq: 500, dur: 0.07, type: "square", gain: 0.16, slideTo: 900 }); },
  shoot() { tone({ freq: 880, dur: 0.09, type: "square", gain: 0.12, slideTo: 220 }); },
  coin() { tone({ freq: 988, dur: 0.06, type: "square", gain: 0.16 }); tone({ freq: 1319, dur: 0.10, type: "square", gain: 0.14, delay: 0.06 }); },
  explode() { tone({ freq: 160, dur: 0.22, type: "sawtooth", gain: 0.22, slideTo: 50 }); },
  powerUp() { [392, 523, 659, 784].forEach((f, i) => tone({ freq: f, dur: 0.09, type: "square", gain: 0.14, delay: i * 0.05 })); },
  win() { [523, 659, 784, 1046].forEach((f, i) => tone({ freq: f, dur: 0.18, type: "triangle", gain: 0.2, delay: i * 0.09 })); },
  lose() { [400, 320, 240].forEach((f, i) => tone({ freq: f, dur: 0.2, type: "sawtooth", gain: 0.16, delay: i * 0.1 })); },
  kaipoche() {  // spoken "Kai po che!" for the kite cut
    try {
      if (window.speechSynthesis && allowed()) {
        const u = new SpeechSynthesisUtterance("Kai po che!");
        u.rate = 1.05; u.pitch = 1.2; u.volume = 1;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(u);
      }
    } catch (e) { /* ignore */ }
  },
  phone() {  // old landline "trrring trrring"
    for (let r = 0; r < 2; r++) {
      const base = r * 0.55;
      for (let i = 0; i < 6; i++) {
        tone({ freq: i % 2 ? 1150 : 950, dur: 0.05, type: "square", gain: 0.13, delay: base + i * 0.045 });
      }
    }
  },
  thunder() { tone({ freq: 80, dur: 0.9, type: "sawtooth", gain: 0.2, slideTo: 40 }); tone({ freq: 120, dur: 0.5, type: "triangle", gain: 0.12, delay: 0.15 }); },
};

// ---------- Looping chiptune background music for arcade games ----------
let _bgm = null;
// simple upbeat 8-bit loop (lead notes in Hz; 0 = rest) + bass
const MELODY = [523, 0, 659, 0, 784, 659, 523, 0, 587, 0, 698, 0, 880, 698, 587, 0,
                523, 0, 659, 0, 784, 880, 988, 0, 880, 784, 659, 587, 523, 0, 392, 0];
const BASS = [131, 131, 165, 165, 196, 196, 165, 165, 147, 147, 175, 175, 220, 220, 175, 175,
              131, 131, 165, 165, 196, 196, 247, 247, 220, 220, 165, 165, 131, 131, 98, 98];
const STEP = 0.16;

export function startBgm() {
  stopBgm();
  const c = ctx();
  if (!c || !allowed()) return;
  const master = c.createGain();
  master.gain.value = 0.06;
  master.connect(c.destination);
  const st = { stopped: false, timer: null, i: 0, master };
  _bgm = st;
  const blip = (freq, type, gain, dur) => {
    if (!freq) return;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.value = freq;
    const t0 = c.currentTime;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g); g.connect(master);
    o.start(t0); o.stop(t0 + dur + 0.02);
  };
  const tick = () => {
    if (st.stopped) return;
    blip(MELODY[st.i % MELODY.length], "square", 0.5, STEP * 0.9);
    blip(BASS[st.i % BASS.length], "triangle", 0.6, STEP * 1.1);
    st.i++;
    st.timer = setTimeout(tick, STEP * 1000);
  };
  tick();
}

export function stopBgm() {
  if (_bgm) {
    _bgm.stopped = true;
    clearTimeout(_bgm.timer);
    try { _bgm.master.disconnect(); } catch (e) { /* ignore */ }
    _bgm = null;
  }
}
export function playStatic(duration = 0.32) {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  const c = new AC();
  const size = Math.floor(c.sampleRate * duration);
  const buffer = c.createBuffer(1, size, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < size; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buffer;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1600;
  bp.Q.value = 0.6;
  const g = c.createGain();
  const t0 = c.currentTime;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.3, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  src.connect(bp);
  bp.connect(g);
  g.connect(c.destination);
  src.start(t0);
  src.stop(t0 + duration);
  setTimeout(() => c.close(), (duration + 0.2) * 1000);
}
